import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy } from '@angular/core';

/**
 * 横に並べた要素が1行に入らないときに、まとめて縦並びに切り替える（入らない1個だけを次の行に落とさない）。
 * 1行に並べたときにはみ出すかを測り、はみ出すときは付けた要素に is-stacked クラスを付ける。
 * 測る間は is-measuring クラスを付けるため、CSS 側で「折り返さずに1行に並べる」指定を is-measuring に書いておく。
 * 縦並びの見た目は CSS 側で is-stacked に書く（画面幅によって切り替えない場合は is-stacked の指定を書かなければよい）。
 *
 * appStackOnWrap に CSS セレクターを渡すと、付けた要素の中のその要素ごとにはみ出しを測る（渡さないときは付けた要素自身を測る）。
 */
@Directive({
  selector: '[appStackOnWrap]',
  standalone: true,
})
export class StackOnWrapDirective implements AfterViewInit, OnDestroy {
  @Input() appStackOnWrap = '';

  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;

  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngAfterViewInit(): void {
    // 測り直しは画面の更新（変更検知）を必要としないため、Angular の外で動かす
    this.zone.runOutsideAngular(() => {
      const host = this.el.nativeElement;
      // 幅が変わったとき・中身が変わったとき・フォントの読み込みが終わったときに測り直す
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(host);
      this.mutationObserver = new MutationObserver(this.update);
      this.mutationObserver.observe(host, { childList: true, subtree: true, characterData: true });
      document.fonts?.ready.then(this.update);
      this.update();
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  private update = (): void => {
    const host = this.el.nativeElement;
    if (!host.isConnected) {
      return;
    }
    host.classList.remove('is-stacked');
    host.classList.add('is-measuring');
    const targets = this.appStackOnWrap
      ? Array.from(host.querySelectorAll<HTMLElement>(this.appStackOnWrap))
      : [host];
    // 端数の誤差で縦並びにならないよう、1px までのはみ出しは入っているとみなす
    const overflow = targets.some(target => target.scrollWidth > target.clientWidth + 1);
    host.classList.remove('is-measuring');
    host.classList.toggle('is-stacked', overflow);
  };
}
